import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// ==================================================================
// Service Worker Registration — Debounced, sem reload loops
// ==================================================================
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js', { updateViaCache: 'none' })
      .then(registration => {
        console.log('Samidel SW: Registado', registration.scope);

        // Verificar atualizações a cada 30 minutos (silenciosamente)
        setInterval(() => {
          registration.update().catch(() => { /* erros transitórios 403/network ignorados */ });
        }, 30 * 60 * 1000);

        // Quando encontrar nova versão, notificar sem forçar reload
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('Samidel SW: Nova versão disponível');
                // Ativar novo SW imediatamente sem reload agressivo
                newWorker.postMessage('SKIP_WAITING');
              }
            });
          }
        });
      })
      .catch(() => {
        // Silenciar erros de SW (403 em ambientes de preview, etc.)
      });
  });

  // Suprimir errors de SW propagados como uncaught runtime errors
  navigator.serviceWorker.addEventListener('error', () => { /* silent */ });

  // Quando um novo SW toma controlo: NUNCA recarregar a página enquanto o utilizador
  // está a usar a app. Antes: requestIdleCallback disparava o reload quase de imediato
  // (o browser fica "idle" entre cliques) → a página recarregava a meio da navegação.
  // Também disparava na 1.ª instalação (clients.claim()), sem haver versão nova.
  //   - 1.ª instalação (página ainda não controlada) → ignorar.
  //   - Versão nova → aviso discreto com botão "Atualizar"; recarrega sozinha apenas se a aba
  //     ficar oculta mais de 2 min (o utilizador não vê nem perde o que estava a fazer).
  const controladaNoArranque = !!navigator.serviceWorker.controller;
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!controladaNoArranque) return; // primeira instalação — não há nada para atualizar
    if (refreshing) return;
    refreshing = true;
    console.log('Samidel SW: Nova versão ativa — atualização adiada até a aba ficar oculta');

    const doReload = () => {
      if (window.__samidel_sw_reloading) return;
      window.__samidel_sw_reloading = true;
      window.location.reload();
    };

    // Só recarrega sozinha se a aba estiver oculta há mais de 2 minutos
    let hiddenTimer = null;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenTimer = setTimeout(doReload, 120_000);
      } else if (hiddenTimer) {
        clearTimeout(hiddenTimer);
        hiddenTimer = null;
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    import('sonner').then(({ toast }) => {
      toast('Nova versão da aplicação disponível', {
        description: 'Atualize quando terminar o que está a fazer.',
        duration: Infinity,
        action: { label: 'Atualizar', onClick: doReload },
      });
    }).catch(() => {});
  });
}

// Capturar erros não tratados de Service Worker fetches (e.g. 403 transitório)
window.addEventListener('unhandledrejection', (event) => {
  const msg = String(event.reason?.message || event.reason || '');
  if (msg.includes('ServiceWorker') || msg.includes('service-worker.js')) {
    event.preventDefault();
  }
});

// Suprimir também o React Dev Overlay (CRA) para erros do Service Worker
window.addEventListener('error', (event) => {
  const msg = String(event.message || event.error?.message || '');
  if (msg.includes('ServiceWorker') || msg.includes('service-worker.js')) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return false;
  }
}, true);

// PWA Install Prompt
let deferredPrompt;
let installPromptShown = false;

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredPrompt = e;

  const lastDismissed = localStorage.getItem('pwa-dismissed');
  const dismissedRecently = lastDismissed && (Date.now() - parseInt(lastDismissed)) < 24 * 60 * 60 * 1000;

  if (!installPromptShown && !dismissedRecently) {
    setTimeout(() => {
      const banner = document.getElementById('pwa-install-banner');
      if (banner && deferredPrompt) {
        banner.style.display = 'flex';
        installPromptShown = true;
      }
    }, 2000);
  }
});

window.installPWA = async () => {
  const banner = document.getElementById('pwa-install-banner');
  if (banner) banner.style.display = 'none';

  if (deferredPrompt) {
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    console.log('Samidel PWA: Install', outcome);
    if (outcome === 'accepted') {
      localStorage.setItem('pwa-installed', 'true');
    }
    deferredPrompt = null;
  }
};

window.dismissPWABanner = () => {
  const banner = document.getElementById('pwa-install-banner');
  if (banner) banner.style.display = 'none';
  localStorage.setItem('pwa-dismissed', Date.now().toString());
};

window.requestNotificationPermission = async () => {
  if ('Notification' in window) {
    return await Notification.requestPermission();
  }
  return 'denied';
};

window.isStandalone = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
